/**
 * Tiny inline-SVG sparkline. Deliberately dependency-free (recharts is reserved
 * for the larger scatter/histogram) so the leaderboard can render dozens cheaply.
 */
interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  className?: string;
  /** Stroke colour; defaults to the brand teal chart token. */
  stroke?: string;
  /** Fixed value range; defaults to the min/max of `values`. */
  min?: number;
  max?: number;
  /** Optional dashed reference line (e.g. the period average), in `values` units. */
  baseline?: number;
  'aria-label'?: string;
}

export function Sparkline({
  values,
  width = 72,
  height = 22,
  className,
  stroke = 'var(--chart-1, #14b8a6)',
  min,
  max,
  baseline,
  'aria-label': ariaLabel,
}: SparklineProps) {
  if (values.length < 2) {
    // Not enough points to draw a line — reserve the space for layout stability.
    return <svg width={width} height={height} className={className} aria-hidden="true" />;
  }

  const lo = min ?? Math.min(...values);
  const hi = max ?? Math.max(...values);
  const span = hi - lo || 1; // avoid divide-by-zero on a flat series
  const pad = 2;
  const stepX = (width - pad * 2) / (values.length - 1);
  const yFor = (value: number) => pad + (1 - (value - lo) / span) * (height - pad * 2);

  const points = values.map((value, i) => `${(pad + i * stepX).toFixed(1)},${yFor(value).toFixed(1)}`);

  const last = points[points.length - 1].split(',');
  const baselineY =
    baseline !== undefined && Number.isFinite(baseline) ? yFor(baseline) : null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      role="img"
      aria-label={ariaLabel}
    >
      {baselineY !== null && (
        <line
          x1={0}
          y1={baselineY.toFixed(1)}
          x2={width}
          y2={baselineY.toFixed(1)}
          stroke="var(--border)"
          strokeWidth={1}
          strokeDasharray="3 3"
        />
      )}
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r={1.8} fill={stroke} />
    </svg>
  );
}
